import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ShieldCheck, Scale, Microscope, Lock, BadgeCheck, HandCoins, ArrowRight } from "lucide-react";
import { Section, SectionHeading, Eyebrow } from "@/components/Section";

const REASONS = [
  { icon: ShieldCheck, t: "100% Independent", d: "No brand affiliations. We don't sell or recommend supplements — we only test them and report the facts." },
  { icon: Scale, t: "No Conflicts of Interest", d: "Our only product is the truth. We have no financial incentive to make any supplement look good or bad." },
  { icon: Microscope, t: "Lab-Grade Methods", d: "We apply the methods appropriate for each product — including HPLC, LC-MS/MS, GC-MS and ICP-MS — the same families of techniques used by other professional analytical labs." },
  { icon: Lock, t: "Secure & Private", d: "Your account, orders, and PDF reports are protected and delivered privately to your dashboard." },
  { icon: HandCoins, t: "Money-Back Guarantee", d: "If results aren't delivered within 14 days of receiving your sample, you get a full refund." },
  { icon: BadgeCheck, t: "Clear, Honest Reports", d: "Plain-English results comparing measured values to label claims. No spin, no marketing." },
];

export default function WhyTrustUs() {
  return (
    <div data-testid="why-trust-us-page">
      <section className="bg-ocean">
        <div className="mx-auto max-w-7xl px-6 py-20 text-center md:px-10 md:py-24">
          <Eyebrow>Independence first</Eyebrow>
          <h1 className="mx-auto mt-5 max-w-3xl text-4xl font-bold tracking-tight text-white sm:text-5xl">Why Trust Us</h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-slate-300">We built TestMySupps on a single promise: completely independent, factual testing.</p>
          <span className="mt-6 inline-flex rounded-full bg-teal/15 px-4 py-2 text-sm font-semibold text-teal-light">100% Independent — No brand affiliations</span>
        </div>
      </section>

      <Section>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {REASONS.map((c, i) => (
            <motion.div key={c.t} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
              className="group rounded-3xl border border-border bg-white p-8 transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_18px_45px_rgba(47,76,120,0.1)]">
              <span className="grid h-12 w-12 place-items-center rounded-xl bg-accent transition-colors duration-300 group-hover:bg-navy"><c.icon className="h-6 w-6 text-teal-dark transition-colors duration-300 group-hover:text-lime" /></span>
              <h3 className="mt-5 text-lg font-semibold text-ocean">{c.t}</h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{c.d}</p>
            </motion.div>
          ))}
        </div>

        <div className="mt-14 overflow-hidden rounded-3xl border border-border bg-white md:grid md:grid-cols-2">
          <img src="https://images.unsplash.com/photo-1638202993928-7267aad84c31?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtZWRpY2FsJTIwcHJvZmVzc2lvbmFsJTIwY2xlYW58ZW58MHx8fHwxNzgwNTU4OTU3fDA&ixlib=rb-4.1.0&q=85" alt="Professional" className="h-full w-full object-cover" />
          <div className="p-8 md:p-12">
            <h2 className="text-2xl font-bold tracking-tight text-ocean">Built for people who want the truth</h2>
            <p className="mt-4 text-slate-600">Whether you're verifying a testosterone booster, a GLP-1 supplement, or a daily vitamin, you deserve to know exactly what you're putting in your body. We give you that — objectively.</p>
            <Link to="/pricing" className="mt-7 inline-flex items-center gap-2 rounded-full bg-ocean px-7 py-3 text-base font-semibold text-white transition-colors hover:bg-ocean-light">Test My Supplements <ArrowRight className="h-5 w-5" /></Link>
          </div>
        </div>
      </Section>
    </div>
  );
}
